import React            from 'react';
import View             from '@widesk-ui/components/View';
import Alert            from '@widesk-ui/components/Alert';
import Button           from '@widesk-ui/components/Button';
import Checkbox         from '@widesk-ui/components/form/Checkbox';
import { EyeOutlined }          from '@ant-design/icons';
import { EyeInvisibleOutlined } from '@ant-design/icons';
import Select           from '@widesk-ui/components/form/Select';
import SelectPlaceType  from '@selects/map/SelectPlaceType';
import { ROLE_LABELS }      from '@/TokamapEditor/helpers/planLayerMapping';
import { ROLE_ORDER }       from '@/TokamapEditor/helpers/planLayerMapping';
import { buildLayerLabels } from '@/TokamapEditor/helpers/planLayerMapping';
import { isPseudoLayerName } from '@/TokamapEditor/helpers/planLayerMapping';
import { layerSwatch }      from '@/TokamapEditor/helpers/planLayerMapping';
import { previewKey }      from '@/TokamapEditor/helpers/planLayerMapping';
import type { PlanCategory } from '@/TokamapEditor/helpers/planLayerMapping';
import { categoryColour }    from '@/TokamapEditor/helpers/planShapes';
import type { DxfLayer }     from '@models/map/dxfTypes';
import type { PlanShape }    from '@models/map/dxfTypes';

/**
 * Rendu de l'étape « calques à importer » — voir `helpers/planLayerMapping.ts` pour ce que
 * l'opérateur peut y changer et pourquoi le serveur l'accepte.
 */
type Props = {
	layers: DxfLayer[];
	categories: PlanCategory[];
	/**
	 * Les formes du plan — la SOURCE de la pastille de couleur de chaque ligne.
	 *
	 * L'écran de tri ne peint plus les formes aux couleurs du document (il parle d'états : ce
	 * qui sera créé, ce qui fait doute, ce qui tombe). La couleur du calque reste pourtant ce
	 * qui le désigne à l'œil sur le plan, et c'est ici qu'elle a sa place.
	 */
	shapes: PlanShape[];
	/** Faux pour une image ou un PDF scanné : aucun calque de numéros à choisir. */
	readsText?: boolean;
	projectUrn: Urn;
	/** Calque actuellement posé sur la carte par le bouton œil, `null` si aucun. */
	previewedLayer: string | null;
	onChange: (key: string, patch: Partial<PlanCategory>) => void;
	/** Coche ou décoche TOUTES les lignes d'un coup. */
	onToggleAll: (enabled: boolean) => void;
	/** Applique un type de POI à toutes les lignes cochées. */
	onApplyPlaceType: (placeTypeUrn: string) => void;
	/** Bascule l'aperçu d'une ligne sur la carte (même ligne = on la retire). */
	onPreviewLayer: (category: PlanCategory) => void;
};

const PlanLayerMapping = ({ layers, categories, shapes, readsText = true, projectUrn, previewedLayer, onChange, onToggleAll, onApplyPlaceType, onPreviewLayer }: Props) => {
	const layersByName = React.useMemo(
		() => new Map(layers.map(layer => [layer.name, layer])),
		[layers],
	);

	// Les pseudo-calques du serveur sont nommés par leur STYLE (`fill rgb(3%, …) · stroke …`) :
	// illisible pour l'opérateur. On n'affiche que « Forme 1 », « Forme 2 »… ; le nom brut reste
	// la clé envoyée au serveur.
	const layerLabels = React.useMemo(() => buildLayerLabels(layers), [layers]);
	const nameOf = React.useCallback(
		(name: string) => layerLabels.get(name) ?? name,
		[layerLabels],
	);

	// Un calque de numéros peut être n'importe quel calque du plan : la détection de rôle est
	// best-effort, on ne s'en sert que pour l'ordre et l'étiquette, jamais pour filtrer.
	const numberOptions = React.useMemo(() => [
		{ value: '', label: 'Aucun (aucun POI ne sera créé)' },
		...[...layers]
			.sort((a, b) => (ROLE_ORDER[b.role] - ROLE_ORDER[a.role])
				|| nameOf(a.name).localeCompare(nameOf(b.name), undefined, { numeric: true }))
			.map(layer => ({ value: layer.name, label: `${nameOf(layer.name)} — ${ROLE_LABELS[layer.role]}` })),
	], [layers, nameOf]);

	const suggestedCount = categories.filter(category => category.suggested).length;

	// Le serveur propose une ligne par FORME sur un plan raster : cocher trente-sept cases
	// puis choisir trente-sept fois le même type de POI n'est pas un travail d'opérateur.
	const enabled = React.useMemo(() => categories.filter(category => category.enabled), [categories]);
	const allEnabled = categories.length > 0 && enabled.length === categories.length;

	// Type commun à toute la sélection, s'il y en a un : le select en tête reflète alors ce
	// qui est réellement appliqué, et redevient vide dès qu'une ligne diverge.
	const sharedPlaceType = React.useMemo(() => {
		const types = new Set(enabled.map(category => category.placeTypeUrn));

		return 1 === types.size ? ([...types][0] || undefined) : undefined;
	}, [enabled]);

	return (
		<View gap={8}>
			{!suggestedCount && (
				<Alert
					type="warning"
					showIcon
					message="Aucune catégorie détectée"
					description="La détection n’a rien reconnu dans ce plan. Cochez vous-même les calques à importer."
				/>
			)}

			{categories.length > 1 && (
				<View gap={6} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
					<View row gap={8} center>
						<Checkbox value={allEnabled} onChange={() => onToggleAll(!allEnabled)} />
						<View flex size={12}>
							{allEnabled ? 'Tout décocher' : 'Tout cocher'}
						</View>
						<View size={11} color="#999">
							{enabled.length} / {categories.length} sélectionnée(s)
						</View>
					</View>

					{enabled.length > 1 && (
						<SelectPlaceType
							projectUrn={projectUrn}
							placeholder={`Appliquer un type de POI aux ${enabled.length} lignes cochées`}
							allowClear
							value={sharedPlaceType}
							onChange={(value?: unknown) => onApplyPlaceType(String(value ?? ''))}
						/>
					)}
				</View>
			)}

			{categories.map(category => {
				const layer = layersByName.get(category.baseLayer);
				// Les formes d'abord, le nom du calque en secours : une ligne qui ne désigne
				// qu'une forme a SA couleur, là où le nom du pseudo-calque ne donne que celle,
				// commune, du style qui les a regroupées. Le nom reprend la main quand aucune
				// forme n'a été transmise — plan tronqué, ou calque de numéros.
				const swatch = categoryColour(shapes, categories, category) ?? layerSwatch(category.baseLayer);
				const key = previewKey(category);
				// Une catégorie qui ne désigne QU'UNE forme porte son propre libellé
				// (« Forme 12 ») : afficher celui du calque donnerait le même texte à toutes
				// les lignes de la même couleur.
				const shapeCount = category.shapes?.length ?? 0;
				const title = shapeCount > 0 || !isPseudoLayerName(category.baseLayer)
					? category.label
					: nameOf(category.baseLayer);

				return (
					<View key={category.key} gap={4} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
						<View row gap={8} center>
							<Checkbox
								value={category.enabled}
								onChange={() => onChange(category.key, { enabled: !category.enabled })}
							/>
							<View flex>
								<View row gap={6} center>
									{swatch && (
										<View style={{
											width: 10,
											height: 10,
											borderRadius: 2,
											background: swatch,
											border: '1px solid #d9d9d9',
											flexShrink: 0,
										}} />
									)}
									{/* Le libellé du serveur EST le nom du calque : sur un pseudo-calque il ne
									    dirait que le style, on lui préfère « Forme N ». */}
									<View size={13}>{title}</View>
								</View>
								<View size={11} color="#999">
									{/* Le compte du CALQUE n'a plus de sens pour une ligne qui ne
									    désigne qu'une de ses formes. */}
									{shapeCount > 0
										? `${shapeCount} forme(s)`
										: (layer ? `${ROLE_LABELS[layer.role]}, ${layer.entities} entité(s)` : category.baseLayer)}
									{!category.suggested && ' — non proposé'}
									{category.remembered && ' — repris d’un import précédent'}
								</View>
							</View>
							{/* Un pseudo-calque n'a pour tout nom que ses couleurs : le voir sur la carte
							    est la seule façon de savoir ce qu'on coche. */}
							<Button
								type="text"
								size="small"
								title={previewedLayer === key
									? 'Ne plus surligner'
									: (shapeCount > 0 ? 'Surligner cette forme sur la carte' : 'Surligner ce calque sur la carte')}
								icon={previewedLayer === key ? <EyeInvisibleOutlined /> : <EyeOutlined />}
								onClick={() => onPreviewLayer(category)}
							/>
						</View>

						{category.enabled && (
							<View gap={4} style={{ paddingLeft: 24 }}>
								<SelectPlaceType
									projectUrn={projectUrn}
									placeholder="Type de POI"
									allowClear
									value={category.placeTypeUrn || undefined}
									onChange={(value?: unknown) => onChange(category.key, { placeTypeUrn: String(value ?? '') })}
								/>
								{readsText && (
									<Select
										options={numberOptions}
										placeholder="Calque des numéros"
										value={category.numberLayer ?? ''}
										onChange={(value: unknown) => onChange(category.key, { numberLayer: String(value ?? '') || null })}
									/>
								)}
								{!readsText ? (
									<View size={11} color="#999">
										Les numéros d’une image ne sont pas lus : les POI seront créés sans
										numéro — à saisir dans l’éditeur.
									</View>
								) : !category.numberLayer && (
									<View size={11} color="#999">
										Sans calque de numéros, les POI seront créés sans numéro — à saisir
										dans l’éditeur.
									</View>
								)}
								{category.briefLayer && (
									<View size={11} color="#999">Équipement : {nameOf(category.briefLayer)}</View>
								)}
							</View>
						)}
					</View>
				);
			})}
		</View>
	);
};

export default PlanLayerMapping;
